// src/components/ProfileImage.jsx
import React, { useState } from 'react';
import { User, Camera, X, Upload } from 'lucide-react';

const ProfileImage = ({ 
  src, 
  alt, 
  size = 'md', 
  editable = false, 
  onImageSelect,
  className = '',
  showUploadOverlay = false
}) => {
  const [imageError, setImageError] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const sizeClasses = {
    xs: 'w-8 h-8',
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
    '2xl': 'w-40 h-40'
  };

  const iconSizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
    '2xl': 'h-16 w-16'
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && onImageSelect) {
      onImageSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/') && onImageSelect) {
      onImageSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className={`relative ${sizeClasses[size]} ${className}`}>
      {/* Profile Image or Placeholder */}
      <div 
        className={`
          ${sizeClasses[size]} rounded-full overflow-hidden bg-gray-100 flex items-center justify-center
          ${editable ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}
          ${dragOver ? 'bg-blue-100 border-2 border-blue-300 border-dashed' : 'border border-gray-200'}
        `}
        onDrop={editable ? handleDrop : undefined}
        onDragOver={editable ? handleDragOver : undefined}
        onDragLeave={editable ? handleDragLeave : undefined}
      >
        {src && !imageError ? (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        ) : (
          <User className={`${iconSizes[size]} text-gray-400`} />
        )}
        
        {/* Upload Overlay */}
        {editable && showUploadOverlay && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full opacity-0 hover:opacity-100 transition-opacity">
            <Camera className="h-4 w-4 text-white" />
          </div>
        )}

        {/* Drag Over Overlay */}
        {editable && dragOver && (
          <div className="absolute inset-0 bg-blue-100 bg-opacity-90 flex items-center justify-center rounded-full">
            <Upload className="h-4 w-4 text-blue-600" />
          </div>
        )}
      </div>

      {/* Edit Button */}
      {editable && (
        <>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id={`profile-upload-${Math.random()}`}
          />
          <label
            htmlFor={`profile-upload-${Math.random()}`}
            className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-lg"
          >
            <Camera className="h-3 w-3" />
          </label>
        </>
      )}
    </div>
  );
};

export default ProfileImage;